import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import axiosInstance from '../axios';
import { API_BASE_URL } from '../../constants';
import { Gender } from '../../types/gender';
import { NewAPIResponse } from '../api-response/legacy-response';
import { UserUpdateRequest } from '../../store/user/types';
import { WorkTimePreference } from '../../store/form/types';
import { JobExperienceRequest } from '../../types/jobExperience';

export class UserAPI {
  userServiceRoute = `${API_BASE_URL}/user`;
  config: AxiosRequestConfig = {};

  constructor() {
    this.config = { withCredentials: true };
  }

  checkPhone = async (
    phone: string,
    phoneCountryCode: string,
  ): Promise<NewAPIResponse<boolean>> => {
    try {
      const response = await axiosInstance.post<NewAPIResponse<boolean>>(
        `${this.userServiceRoute}/auth/check-phone`,
        {
          phone,
          phoneCountryCode,
        },
      );

      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<any>;
        const errorData = error.response?.data;

        if (errorData?.message && Array.isArray(errorData.message)) {
          const errorMessage = errorData.message.join(', ');
          throw new Error(errorMessage);
        }

        if (errorData?.payload?.message) {
          throw new Error(errorData.payload.message);
        }

        if (errorData?.message && typeof errorData.message === 'string') {
          throw new Error(errorData.message);
        }

        throw new Error('common.somethingWentWrong');
      }

      throw new Error('common.somethingWentWrong');
    }
  };

  checkEmail = async (email: string): Promise<boolean> => {
    try {
      const result = await axiosInstance.post<NewAPIResponse<boolean>>(
        `${this.userServiceRoute}/auth/check-email`,
        {
          email,
        },
      );

      return result.data.payload ?? false;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<any>;
        const errorData = error.response?.data;

        if (errorData?.message && typeof errorData.message === 'string') {
          throw new Error(errorData.message);
        }

        if (errorData?.message && Array.isArray(errorData.message)) {
          const errorMessage = errorData.message.join(', ');
          throw new Error(errorMessage);
        }

        throw new Error('common.somethingWentWrong');
      }

      throw new Error('common.somethingWentWrong');
    }
  };

  submitVerificationCode = async (
    code: string,
    phone: string,
    phoneCountryCode: string,
  ): Promise<NewAPIResponse<{ action: string; jwtToken?: string }>> => {
    try {
      const response = await axiosInstance.post<
        NewAPIResponse<{ action: string; jwtToken: string }>
      >(`${this.userServiceRoute}/auth/phone-confirm`, {
        code,
        phone,
        phoneCountryCode,
      });

      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<any>;
        const errorData = error.response?.data;

        if (errorData?.message && Array.isArray(errorData.message)) {
          const errorMessage = errorData.message.join(', ');
          throw new Error(errorMessage);
        }

        if (errorData?.message && typeof errorData.message === 'string') {
          throw new Error(errorData.message);
        }

        throw new Error('common.somethingWentWrong');
      }

      throw new Error('common.somethingWentWrong');
    }
  };

  registerUser = async (
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    phone: string,
    birthDate: string, // YYYY-MM-DD
    gender: Gender,
    location?: string,
  ): Promise<NewAPIResponse<{ jwtToken: string }>> => {
    try {
      const response = await axiosInstance.post<NewAPIResponse<{ jwtToken: string }>>(
        `${this.userServiceRoute}/auth/register`,
        {
          baseType: 'jobseeker',
          email,
          firstName,
          lastName,
          password,
          phone,
          birthDate,
          gender,
          location,
        },
      );

      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<any>;
        const errorData = error.response?.data;

        // Handle array of error messages
        if (errorData?.message && Array.isArray(errorData.message)) {
          const errorMessage = errorData.message.join(', ');
          throw new Error(errorMessage);
        }

        // Handle error message in payload (as shown in your example)
        if (errorData?.payload?.message) {
          throw new Error(errorData.payload.message);
        }

        // Handle direct message property
        if (errorData?.message && typeof errorData.message === 'string') {
          throw new Error(errorData.message);
        }

        // Handle status code specific errors
        if (error.response?.status === 400) {
          throw new Error('common.badRequest');
        }

        if (error.response?.status === 409) {
          throw new Error('common.conflict');
        }

        if (error.response?.status === 422) {
          throw new Error('common.validationError');
        }

        throw new Error('common.somethingWentWrong');
      }

      throw new Error('common.somethingWentWrong');
    }
  };

  me = async () => {
    try {
      const result = await axiosInstance.get<any>(`${this.userServiceRoute}/me`);
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotGetMe');
    }
  };

  getFormInputList = async (jobSeekerProfileId: string) => {
    try {
      const result = await axiosInstance.get<any>(
        `${this.userServiceRoute}/jobseeker-profile/${jobSeekerProfileId}/onboarding`,
      );

      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotGetFormInputList');
    }
  };

  getWorkTimePreferences = async (): Promise<NewAPIResponse<WorkTimePreference[]>> => {
    try {
      const result = await axiosInstance.get<NewAPIResponse<WorkTimePreference[]>>(
        `${this.userServiceRoute}/work-time-preference`,
      );
      return result.data;
    } catch (e) {
      throw new Error('apiResponse.couldNotGetWorkTimePreferences');
    }
  };

  updateUser = async (user: UserUpdateRequest) => {
    try {
      const result = await axiosInstance.put<any>(`${this.userServiceRoute}/me`, {
        jobSeeker: {
          ...user,
        },
      });
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotUpdateUser');
    }
  };

  addExperience = async (experienceRequest: JobExperienceRequest) => {
    try {
      const result = await axiosInstance.post<any>(
        `${this.userServiceRoute}/experience`,
        experienceRequest,
      );
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotAddExperience');
    }
  };

  updateExperience = async (experienceId: number, experienceRequest: JobExperienceRequest) => {
    try {
      const result = await axiosInstance.put<any>(
        `${this.userServiceRoute}/experience/${experienceId}`,
        experienceRequest,
      );
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotUpdateExperience');
    }
  };

  deleteExperience = async (experienceId: number) => {
    try {
      const result = await axiosInstance.delete<any>(
        `${this.userServiceRoute}/experience/${experienceId}`,
      );
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotDeleteExperience');
    }
  };

  addExperienceCompany = async (companyName: string) => {
    try {
      const result = await axiosInstance.post<any>(`${this.userServiceRoute}/experience-company`, {
        companyName,
      });
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotAddExperienceCompany');
    }
  };

  getDrivingLicenses = async () => {
    try {
      const result = await axiosInstance.get<any>(`${this.userServiceRoute}/driving-licenses`);
      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotGetDrivingLicenses');
    }
  };
}
